/*
 * Copyright (c) 2025 by PROS, Inc.  All Rights Reserved.
 * This software is the confidential and proprietary information of
 * PROS, Inc. ("Confidential Information").
 * You may not disclose such Confidential Information, and may only
 * use such Confidential Information in accordance with the terms of
 * the license agreement you entered into with PROS.
 */
import { PwcAiChatControllerBuilder } from './controller/pwc-ai-chat-controller-builder.js';
import { debug } from './internal/debug.js';
import { resolveDisplayLocale } from './internal/languages.js';
import { SupportedLocale, } from './model.js';
/**
 * Represents a global service accessible from everywhere.
 */
export class ProsAiChatUi {
    constructor() {
        // the current language.
        this._currentLocale = SupportedLocale.en_US;
        // store the list of ai button elements with associated event handler.
        this._pwcAiButtons = [];
        // custom renderers.
        this._customRenderers = new Map();
        // event listeners.
        this._eventListeners = new Map();
        // authentication status.
        this._isAuthenticated = false;
        // logger.
        this._log = debug('ProsAiChatUi');
    }
    /**
     * Register the chat element.
     */
    registerPwcAiChat(element) {
        this._log('register chat', element);
        // guard.
        if (this._pwcAiChat) {
            throw new Error('Illegal state: Chat already registered, only one chat per page is allowed.');
        }
        // attach handler.
        const postMessageHandler = (event) => {
            this._withChatAndController((controller, chat) => {
                controller.handlePostMessage(event, chat);
            });
        };
        // attach handler.
        const retryPostMessageHandler = () => {
            this._withChatAndController((controller, chat) => {
                controller.handleRetryPostMessage(chat);
            });
        };
        // attach handler.
        const userReactionHandler = (event) => {
            this._withChatAndController((controller, chat) => {
                controller.handleReaction(event.detail.queryId, event.detail.reaction, chat);
            });
        };
        // attach handler.
        const userFeedbackHandler = (event) => {
            this._withChatAndController(controller => {
                controller.handleFeedbackComment(event.detail.queryId, event.detail.feedbackComment);
            });
        };
        // attach event listener.
        element.addEventListener('pwc-ai-chat-post-message', postMessageHandler);
        element.addEventListener('pwc-ai-chat-retry-post-message', retryPostMessageHandler);
        element.addEventListener('pwc-ai-chat-user-reaction', userReactionHandler);
        element.addEventListener('pwc-ai-chat-user-feedback', userFeedbackHandler);
        // udpate locale.
        element.locale = this._currentLocale;
        // save refrences.
        this._pwcAiChat = {
            element,
            postMessageHandler,
            retryPostMessageHandler,
            userReactionHandler,
            userFeedbackHandler,
        };
    }
    /**
     * Unregister the chat element.
     */
    unregisterPwcAiChat(element) {
        this._log('unregister chat', element);
        // guard.
        if (!this._pwcAiChat || this._pwcAiChat.element !== element) {
            throw new Error('Illegal state: Unknown chat element.');
        }
        // remove event listener.
        element.removeEventListener('pwc-ai-chat-post-message', this._pwcAiChat.postMessageHandler);
        // remove event listener.
        element.removeEventListener('pwc-ai-chat-retry-post-message', this._pwcAiChat.retryPostMessageHandler);
        // remove event listener.
        element.removeEventListener('pwc-ai-chat-user-reaction', this._pwcAiChat.userReactionHandler);
        // remove event listener.
        element.removeEventListener('pwc-ai-chat-user-feedback', this._pwcAiChat.userFeedbackHandler);
        // delete element and controller.
        this._pwcAiChat = undefined;
    }
    /**
     * Register the chat panel element.
     */
    registerPwcAiChatPanel(element) {
        this._log('register chat panel', element);
        // guard.
        if (this._pwcAiChatPanel) {
            throw new Error('Illegal state: Chat Panel already registered, only one panel per page is allowed.');
        }
        // create handler.
        const newChatHandler = () => {
            this._withChatAndController((controller, chat) => {
                controller.handleNewChatSession(chat);
            });
        };
        // attach event listener.
        element.addEventListener('pwc-ai-chat-panel-new-chat', newChatHandler);
        // udpate locale.
        element.locale = this._currentLocale;
        // save references.
        this._pwcAiChatPanel = { element, newChatHandler };
    }
    /**
     * Unregister the chat panel element.
     */
    unregisterPwcAiChatPanel(element) {
        this._log('unregister chat panel', element);
        // guard.
        if (!this._pwcAiChatPanel || this._pwcAiChatPanel.element !== element) {
            throw new Error('Illegal state: Unknown chat panel element.');
        }
        // remove event listener.
        element.removeEventListener('pwc-ai-chat-panel-new-chat', this._pwcAiChatPanel.newChatHandler);
        // delete element.
        this._pwcAiChatPanel = undefined;
    }
    /**
     * Register the chat docked panel element.
     */
    registerPwcAiChatOverlay(element) {
        this._log('register chat overlay', element);
        // guard.
        if (this._pwcAiChatOverlay) {
            throw new Error('Illegal state: Chat Overlay already registered, only one panel per page is allowed.');
        }
        // udpate locale.
        element.locale = this._currentLocale;
        // save references.
        this._pwcAiChatOverlay = element;
    }
    /**
     * Unregister the chat overlay element.
     */
    unregisterPwcAiChatOverlay(element) {
        this._log('unregister chat overlay', element);
        // guard.
        if (!this._pwcAiChatOverlay || this._pwcAiChatOverlay !== element) {
            throw new Error('Illegal state: Unknown chat overlay element.');
        }
        // delete element.
        this._pwcAiChatOverlay = undefined;
    }
    /**
     * Register an ai button element.
     */
    registerPwcAiButton(element) {
        this._log('register ai button', element);
        // index in the registered elements.
        const existingElement = this._pwcAiButtons.find(reference => reference.element === element);
        // guard.
        if (existingElement) {
            throw new Error('Illegal state: ai button already registered, only one chat per page is allowed.');
        }
        // hide the button if not authenticated.
        element.isHidden = !this._isAuthenticated;
        // create handler.
        const openChatHandler = (event) => {
            // open the overlay.
            if (this._pwcAiChatOverlay) {
                this._pwcAiChatOverlay.open();
            }
            // associate discussion.
            this._withChatAndController((controller, chat) => {
                controller.handleOpenChat(event, chat);
            });
        };
        // attach event listener.
        element.addEventListener('pwc-ai-button-open-chat', openChatHandler);
        // create handler.
        const contextChangeHandler = (event) => {
            // associate discussion.
            this._withChatAndController(controller => {
                controller.handleContextChange(event);
            });
        };
        // attach event listener.
        element.addEventListener('pwc-ai-button-context-change', contextChangeHandler);
        // save references.
        this._pwcAiButtons.push({ element, openChatHandler, contextChangeHandler });
    }
    /**
     * Unregister an ai button element.
     */
    unregisterPwcAiButton(element) {
        this._log('unregister ai button', element);
        // index in the registered elements.
        const reference = this._pwcAiButtons.find(it => it.element === element);
        // guard.
        if (!reference) {
            throw new Error('Illegal state: Unknown ai button element.');
        }
        // remove event listener.
        reference.element.removeEventListener('pwc-ai-button-open-chat', reference.openChatHandler);
        reference.element.removeEventListener('pwc-ai-button-context-change', reference.contextChangeHandler);
        // find index and...
        const index = this._pwcAiButtons.indexOf(reference);
        // ...remove it.
        this._pwcAiButtons.splice(index, 1);
    }
    /**
     * Execute a callback with the existing controller instance.
     * @param cb the callback to execute.
     */
    _withChatAndController(cb) {
        // guard.
        if (!this._controller) {
            return;
        }
        // guard.
        if (!this._pwcAiChat) {
            return;
        }
        cb(this._controller, this._pwcAiChat.element);
    }
    /**
     * Configure the chat ui.
     */
    init(settings) {
        if (this._controller) {
            // for debug purpose.
            this._log('a controller has already been registered, creating a new one.');
        }
        // create the controller.
        this._controller = new PwcAiChatControllerBuilder()
            .withAuthenticationToken(settings.authenticationToken)
            .withHttpEndpoint(settings.httpEndpoint)
            .withFlowToken(settings.flowToken)
            .build();
        // set the current language.
        this._currentLocale = resolveDisplayLocale(SupportedLocale.en_US);
        return this._updateAuthenticationSatus(settings.authenticationToken);
    }
    /**
     * Authentication check.
     */
    async _updateAuthenticationSatus(authenticationToken) {
        // try to get the auth token first.
        if (typeof authenticationToken === 'function') {
            try {
                // get the token.
                await authenticationToken();
                // authentication success.
                this._isAuthenticated = true;
            }
            catch (error) {
                // authentication failed.
                this._log('unable to authenticate.');
            }
        }
        else {
            // trivial case : already authenticated with a token.
            this._isAuthenticated = true;
        }
        // update already registered ai buttons.
        for (const pwcAiButton of this._pwcAiButtons) {
            pwcAiButton.element.isHidden = !this._isAuthenticated;
        }
        return Promise.resolve(undefined);
    }
    /**
     * Set the UI Language.
     */
    setUiLanguage(locale) {
        this._currentLocale = locale;
        this._updateLanguage();
    }
    /**
     * Set the UI Language.
     */
    _updateLanguage() {
        // guard.
        if (!this._currentLocale) {
            return;
        }
        // update chat.
        if (this._pwcAiChat) {
            this._pwcAiChat.element.locale = this._currentLocale;
        }
        // update panel.
        if (this._pwcAiChatPanel) {
            this._pwcAiChatPanel.element.locale = this._currentLocale;
        }
        // udpate overlay.
        if (this._pwcAiChatOverlay) {
            this._pwcAiChatOverlay.locale = this._currentLocale;
        }
    }
    close() {
        // guard.
        if (!this._pwcAiChatOverlay) {
            return;
        }
        this._pwcAiChatOverlay.close();
    }
    /**
     * Register a renderer.
     */
    registerRenderer(name, renderer) {
        this._log(`register renderer [name:${name}]`);
        this._customRenderers.set(name, renderer);
    }
    /**
     * Unregister a renderer.
     */
    unregisterRenderer(name) {
        this._log(`unregister renderer [name:${name}]`);
        this._customRenderers.delete(name);
    }
    /**
     * Render an element according it's name.
     */
    render(name, data) {
        const renderer = this._customRenderers.get(name);
        // no custom renderer found.
        if (!renderer) {
            return undefined;
        }
        // use custom renderer.
        try {
            return renderer(data);
        }
        catch (e) {
            this._log('unable to call renderer', e);
        }
        return undefined;
    }
    /**
     * Register an event listener.
     */
    registerEventListener(name, listener) {
        this._log(`register event listener [name:${name}]`);
        this._eventListeners.set(name, listener);
    }
    /**
     * Unregister an event listener.
     */
    unregisterEventListener(name) {
        this._log(`unregister event listener [name:${name}]`);
        this._eventListeners.delete(name);
    }
    /**
     * Notify an event listener according it's name.
     */
    notifyEventListener(name, data) {
        const listener = this._eventListeners.get(name);
        // no listener found.
        if (!listener) {
            return;
        }
        // call listener.
        try {
            listener(data);
        }
        catch (e) {
            this._log('unable to call listener.', e);
        }
    }
    /**
     * Set the chat prompt.
     */
    setPrompt(prompt) {
        this._withChatAndController((_controller, chat) => {
            chat.setPrompt(prompt);
        });
    }
    /**
     * Send a prompt.
     */
    sendPrompt(prompt, context) {
        this._withChatAndController((controller, chat) => {
            controller.handleSendPrompt(prompt, chat, context);
        });
    }
}
// eslint-disable-next-line vars-on-top, import/no-mutable-exports, no-var
export var prosAiChatUi = new ProsAiChatUi();
// eslint-disable-next-line no-undef
globalThis.prosAiChatUi = prosAiChatUi;
//# sourceMappingURL=pros-ai-chat-ui.js.map